import {
  Cancel as CancelIcon,
  Diversity3,
  Mail,
  PersonAdd,
  Save as SaveIcon,
} from "@mui/icons-material";
import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import fetchSingleCustomer from "api/companies/fetchSingleCustomer";
import ConfirmationModal from "components/ConfirmationModal";
import ConfirmPanelChanges from "components/ConfirmPanelChanges";
import CustomCard from "components/CustomCard";
import DataList from "components/DataList";
import InterviewAddApplicantModal from "components/InterviewAddApplicantModal";
import InterviewAddExternalTeamMemberModal from "components/InterviewAddExternalTeamMemberModal";
import MDTypography from "components/MDTypography";
import PanelActions from "components/PanelActions";
import Searchbar from "components/Searchbar";
import { useAppContextController } from "context/AppContext";
import { eventContent } from "data/event";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import fetchSingleJob from "layouts/pages/companyjobs/actions/fetchSingleJob";
import fetchSingleEvent from "layouts/pages/events/actions/fetchSingleEvent";
import updateEvent from "layouts/pages/events/actions/updateEvent";
import { BulkMessagingModal } from "layouts/pages/events/components/BulkMessagingModal";
import updateAvailabilityBlock from "layouts/pages/profile/actions/updateAvailabilityBlock";
import fetchUsers from "layouts/pages/users/actions/fetchUsers";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getDirtyFields } from "utils/helpers/formHelpers";
import { customPanelCard, cardStyles as styles } from "../../styles";
import InterviewApplicantRow from "./InterviewApplicantRow";
import InterviewTeamMemberRow from "./InterviewTeamMemberRow";

const useStyle = makeStyles({
  editor: {
    height: 275,
  },
  previewButton: {
    padding: 0,
  },
});

const InterviewAttendees = ({ setPreviewContent, setEventPreview, setOpen }) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyle();
  const queryClient = useQueryClient();
  const {
    currentEvent,
    setCurrentEvent,
    isMobile,
    changePanel,
    setCurrentFormDirty,
    currentLoggedUser,
  } = useAppContextController();

  const isEdit = !!currentEvent?._id;

  const resolver = useYupValidationResolver(eventContent);
  const {
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    control,
    setError,
    trigger,
    watch,
    getValues,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm({ mode: "onBlur", defaultValues: currentEvent, resolver });

  const [reminderToSaveChangesModalOpen, setReminderToSaveChangesModalOpen] = useState(false);
  const [interviewTypesList, setInterviewTypesList] = useState();
  const [inviteAttendeeModalOpen, setInviteAttendeeModalOpen] = useState(false);
  const [currentAttendee, setCurrentAttendee] = useState(null);
  const [attendeeIndex, setAttendeeIndex] = useState(null);
  const [selectedApplicantIndex, setSelectedApplicantIndex] = useState(null);
  const [bulkMessageModalCurrentMode, setBulkMessageModalCurrentMode] = useState(null);
  const [confirmationModalMode, setConfirmationModalMode] = useState(null);
  const [feedbackModalSelectedData, setFeedbackModalSelectedData] = useState(null);
  const [addApplicantModalOpen, setAddApplicantModalOpen] = useState(false);
  const [addExternalTeamMemberModalOpen, setExternalTeamMemberModalOpen] = useState(false);
  const [currentExternalTeamMember, setCurrentExternalTeamMember] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const allOptions = {
    fetchAll: true,
    filters: {
      _id: [...(currentEvent?.applicants ?? []), ...(currentEvent?.attendees ?? [])]
        .flatMap((app) =>
          app.applicantId || app.id || app._id ? [app.applicantId || app.id || app._id] : []
        )
        ?.join(";"),
    },
  };
  const {
    data: applicants,
    isLoading,
    refetch: refetchApplicants,
  } = useQuery(
    ["interviewApplicants", allOptions],
    () => {
      return fetchApplicants(allOptions);
    },
    {
      enabled: !![...(currentEvent?.applicants ?? []), ...(currentEvent?.attendees ?? [])].length,
    }
  );

  const { data: interviewJob, isLoading: jobIsLoading } = useQuery(
    ["interviewJob", currentEvent?.jobSlug],
    () => {
      return fetchSingleJob(currentEvent?.jobSlug);
    },
    {
      enabled: !!currentEvent?.jobSlug,
    }
  );

  const {
    data: interviewCustomer,
    isLoading: customerIsLoading,
    isFetching: isFetchingCustomer,
  } = useQuery(
    ["interviewCustomer", interviewJob?.data?.job?.companySlug],
    () => {
      return fetchSingleCustomer({ slug: interviewJob?.data?.job?.companySlug });
    },
    {
      enabled: !!interviewJob?.data?.job?.companySlug,
    }
  );

  const jobAttachments = useMemo(() => {
    const newAtt = [];
    if (interviewCustomer?.data?.attachments?.length) {
      newAtt.push(
        ...interviewCustomer.data.attachments.map((att) => ({
          entity: "Customer",
          group: "Customer",
          filename: att.filename,
          companySlug: interviewCustomer.data.slug,
        }))
      );
    }
    if (interviewJob?.data?.job?.attachments?.length) {
      newAtt.push(
        ...interviewJob.data.job.attachments.map((att) => ({
          entity: "Job",
          group: "Job",
          filename: att.filename,
          jobSlug: currentEvent?.jobSlug,
        }))
      );
    }
    return newAtt;
  }, [
    currentEvent?.jobSlug,
    interviewCustomer?.data?.attachments,
    interviewCustomer?.data?.slug,
    interviewJob?.data?.job?.attachments,
  ]);

  const [applicantInfoList, applicantAttachments] = useMemo(() => {
    if (!applicants?.data) return [];
    const applicantsIndexedById = {};
    const allAttachments = [];
    (currentEvent?.applicants ?? []).forEach((appl) => {
      const id = appl.id || appl._id;
      applicantsIndexedById[id] = appl;
    });
    const result = applicants.data?.filter((appl) => {
      const shouldInclude = !!applicantsIndexedById[appl._id];
      if (shouldInclude) {
        allAttachments.push(
          ...(appl.attachments?.map((att) => ({
            ...att,
            filename: att.filename || att.name,
            group: `${appl.firstName} ${appl.lastName}`,
            entity: "Applicant",
            applicantId: appl._id,
          })) ?? [])
        );
      }
      return shouldInclude;
    });
    return [result, allAttachments];
  }, [applicants?.data, currentEvent?.applicants]);

  const teamMemberInfoList = useMemo(() => {
    if (!applicants?.data) return [];
    const applicantsIndexedById = {};
    (currentEvent?.attendees ?? []).forEach((usr) => {
      const id = usr.status === "Team Member" ? usr._id : usr.applicantId;
      applicantsIndexedById[id] = usr;
    });
    return applicants.data?.filter((appl) => !!applicantsIndexedById[appl._id]);
  }, [applicants?.data, currentEvent?.attendees]);

  const currentFormApplicants = watch("applicants");

  const enrichedApplicants = useMemo(() => {
    if (!applicants || !applicants?.data?.length) return currentFormApplicants ?? [];
    return (
      currentFormApplicants?.map((ap) => {
        if (ap.id) {
          const fetchedApplicant = applicants.data.find((fapp) => fapp._id === ap.id);
          if (fetchedApplicant) {
            return {
              ...ap,
              _id: fetchedApplicant._id,
              attachments: fetchedApplicant.attachments,
              notes: fetchedApplicant.notes,
              // interviews: fetchedApplicant.interviews,
            };
          }
        }
        return ap;
      }) ?? []
    );
  }, [applicants, currentFormApplicants]);

  const sortUsers = (a, b) => {
    if (a.firstName > b.firstName) return 1;
    return -1;
  };

  const openPreviewModal = (content) => {
    setPreviewContent(content);
    setEventPreview(currentEvent);
    setOpen(true);
  };

  const cancelChangesHandler = () => {
    const url = currentEvent?.eventUrl;
    reset(currentEvent);
    enqueueSnackbar("Changes have been abandoned!", { variant: "success" });
  };

  useEffect(() => {
    reset(currentEvent);
  }, [currentEvent]);

  const panelActions = (
    <Box mt={2}>
      <Button
        variant="text"
        startIcon={<CancelIcon />}
        onClick={cancelChangesHandler}
        disabled={isSubmitting}
        color="error"
        sx={{ color: "#F44335", fontSize: 14 }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        variant="text"
        endIcon={<SaveIcon />}
        sx={{ color: "#4CAF50", fontSize: 14, lineHeight: 1.5 }}
        disabled={isSubmitting}
      >
        Save
      </Button>
    </Box>
  );

  const searchColumns = [
    { title: "Last Name", field: "lastName", width: 150 },
    { title: "First Name", field: "firstName", width: 150 },
    { title: "Email", field: "email", width: 200 },
    { title: "Phone", field: "phone", width: 200 },
  ];

  const searchColumnsUsers = [
    { title: "Last Name", field: "lastName", width: 150 },
    { title: "First Name", field: "firstName", width: 150 },
    { title: "Email", field: "emailAddress", width: 350 },
  ];

  const updateEventMutation = useMutation(updateEvent, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, { data }) => {
      const updatedEvent = await fetchSingleEvent({ eventUrl: currentEvent?.eventUrl });
      if (updatedEvent) {
        setCurrentEvent({
          ...currentEvent,
          ...data,
          attendees: updatedEvent.attendees,
          applicants: updatedEvent.applicants,
          eventId: currentEvent.eventId,
          eventUrl: currentEvent.eventUrl,
        });
      } else {
        setCurrentEvent({
          ...currentEvent,
          ...data,
          eventId: currentEvent.eventId,
          eventUrl: currentEvent.eventUrl,
        });
      }
      enqueueSnackbar("Event has been updated!", { variant: "success" });
      await queryClient.invalidateQueries("events");
    },
  });

  const updateUserAvailabilityBlockMutation = useMutation(updateAvailabilityBlock, {
    onError: (error) =>
      enqueueSnackbar(`Something went wrong! ${error.toString()}`, { variant: "error" }),
    onSuccess: async (_, { data }) => {
      enqueueSnackbar("User has been updated!", { variant: "success" });
    },
  });

  const editEventFormHandler = async (values) => {
    if (!currentEvent.eventId) {
      enqueueSnackbar("Cannot Update Event -- No Event ID!", { variant: "error" });
      return;
    }
    const modifiedFields = getDirtyFields(values, dirtyFields);

    await updateEventMutation.mutateAsync({
      venueSlug: currentEvent.venueSlug,
      eventId: currentEvent._id,
      data: {
        ...modifiedFields,
      },
    });
  };

  const onChangeFeedback = async ({
    applicant: changeApplicant,
    user: changeUser,
    eventUrl: changeEventUrl,
    rating: changeRating,
    note: changeNote,
    isFinal: changeIsFinal,
  }) => {
    const eventApplicants = [...currentEvent.applicants];

    const idx = enrichedApplicants.findIndex(
      (eap) => (eap._id ?? eap.id) === (changeApplicant._id ?? changeApplicant.id)
    );

    const userId = changeUser?.id ?? changeUser?._id;

    if (idx < 0) return;

    const selectedApplicant = eventApplicants[idx];
    const applicantFeedback = selectedApplicant?.feedback ?? [];

    // If this person hasn't been added to the interview, add it locally
    const currentTeamMembers = getValues("attendees") || [];
    const foundTeamMember = currentTeamMembers.find((tm) => tm.id === userId || tm.id === userId);

    if (!eventApplicants[idx]) return;
    if (!eventApplicants[idx].feedback) eventApplicants[idx].feedback = [];

    if (changeRating === "None") {
      // New status is None. Remove the item that matches the current logged user
      if (changeIsFinal) {
        // This is a final decision, update the main feedback instead
        eventApplicants[idx].interviews = eventApplicants[idx].interviews?.map((int) =>
          int.eventUrl === changeEventUrl ? { ...int, feedback: null } : int
        );
      } else {
        eventApplicants[idx].feedback = applicantFeedback.filter(
          (applFeed) => applFeed.createAgent !== userId
        );
      }
    } else if (changeIsFinal) {
      // This is a final decision, update the main feedback instead
      eventApplicants[idx].interviews = eventApplicants[idx].interviews?.map((int) =>
        int.eventUrl === changeEventUrl
          ? {
              ...int,
              feedback: {
                createAgent: currentLoggedUser?._id,
                agent: `${currentLoggedUser?.firstName} ${currentLoggedUser?.lastName}`,
                firstName: currentLoggedUser?.firstName,
                lastName: currentLoggedUser?.lastName,
                eventUrl: currentEvent?.eventUrl,
                modifiedDate: new Date().toISOString(),
                rating: changeRating,
              },
            }
          : int
      );
    } else {
      const selectedFeedbackIndex = applicantFeedback.findIndex(
        (applFeed) => applFeed.createAgent === userId
      );

      if (selectedFeedbackIndex >= 0) {
        // Feedback exists, change status
        eventApplicants[idx].feedback[selectedFeedbackIndex].rating = changeRating;
      } else {
        // Feedback doesn't exist. Add it
        if (eventApplicants[idx].feedback == null) {
          eventApplicants[idx].feedback = [];
        }
        eventApplicants[idx].feedback.push({
          createAgent: changeUser?._id,
          agent: `${changeUser?.firstName} ${changeUser?.lastName}`,
          firstName: changeUser?.firstName,
          lastName: changeUser?.lastName,
          eventUrl: currentEvent?.eventUrl,
          modifiedDate: new Date().toISOString(),
          rating: changeRating,
        });
      }
    }

    if (changeRating !== "None" && !changeIsFinal && !foundTeamMember) {
      // Team member not found, refresh event
      const updatedEvent = await fetchSingleEvent({ eventUrl: currentEvent?.eventUrl });
      if (updatedEvent) {
        setCurrentEvent({
          ...currentEvent,
          attendees: updatedEvent.attendees,
          applicants: eventApplicants,
        });
      }
    } else {
      setCurrentEvent({
        ...currentEvent,
        applicants: eventApplicants,
      });
    }
  };

  const inviteAdditionalAttendee = () => {
    setInviteAttendeeModalOpen(true);
  };

  const attendeeHandler = (row, isOpenInvitedAttendee, posIdx) => {
    setCurrentAttendee(row);
    setInviteAttendeeModalOpen(isOpenInvitedAttendee);
    setAttendeeIndex(posIdx);
  };

  const setApplicantDetails = (item) => {
    return {
      id: item?._id,
      status: "Roster",
      dateModified: new Date().toISOString(),
      agent: `${currentLoggedUser.firstName} ${currentLoggedUser.lastName}`,
      createAgent: currentLoggedUser._id,
      // eventUrl: currentEvent.url,
      firstName: item?.firstName,
      lastName: item?.lastName,
      email: item?.email,
      phone: item?.phone,
      feedback: item?.feedback,
    };
  };

  const formatSearchResults = (searchResult) => {
    const formatted = searchResult?.map((v, index) => {
      if (v.status === "Deleted") return v;
      if (v.doNotMerge === "Yes") return { ...v, mergeStatus: "ignore" };
      const result = searchResult.find(
        (val, idx) =>
          val.firstName === v.firstName &&
          val.lastName === v.lastName &&
          v._id !== val._id &&
          val.doNotMerge !== "Yes" &&
          val.status !== "Deleted"
      );
      if (result) {
        return { ...v, mergeStatus: "merge" };
      }
      return v;
    });
    return formatted;
  };

  const hasSomeUpdateApplicantList = (updatedApplicants) => {
    if (updatedApplicants?.length !== currentEvent?.applicants?.length) return true;
    return updatedApplicants?.some(
      (updatedApplicant) =>
        !!currentEvent?.applicants?.some(
          (initialApplicant) => initialApplicant.id !== updatedApplicant.id
        )
    );
  };

  const removeAttendee = (idx) => {
    const temporaryAttendees = getValues()?.attendees;
    temporaryAttendees.splice(idx, 1);
    setValue("attendees", temporaryAttendees, { shouldDirty: true });
    if (isEdit) setReminderToSaveChangesModalOpen(true);
  };

  const handleRemoveApplicant = (idx) => {
    const temporaryApplicants = getValues("applicants");
    const updatedApplicants = temporaryApplicants.filter((_, index) => index !== idx);
    if (hasSomeUpdateApplicantList(updatedApplicants)) {
      setValue("applicants", updatedApplicants, { shouldDirty: true });
      if (isEdit) setReminderToSaveChangesModalOpen(true);
    } else {
      setValue("applicants", updatedApplicants, { shouldDirty: false });
    }
  };

  const handleConfirmChanges = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmit(editEventFormHandler)();
      changePanel();
      setCurrentFormDirty(false);
    }
  };

  const handlePressBulkMessageButton = (mode) => {
    if (isDirty) {
      setConfirmationModalMode(mode);
    } else {
      setBulkMessageModalCurrentMode(mode);
    }
  };

  const handleAddApplicant = (item) => {
    if (
      !getValues("applicants").find((applicant) => {
        const applId = applicant._id || applicant.id;
        return applId === item._id;
      })
    ) {
      setValue("applicants", [...getValues("applicants"), setApplicantDetails(item)], {
        shouldDirty: true,
      });
      if (isEdit) setReminderToSaveChangesModalOpen(true);
      enqueueSnackbar("Applicant added to the list", { variant: "success" });
    } else enqueueSnackbar("Applicant is already on the list", { variant: "error" });
  };

  const handleOpenAddExternalTeamMembers = (params) => {
    setExternalTeamMemberModalOpen(true);
    setCurrentExternalTeamMember(null);
  };

  const handleCloseExternalTeamMembers = (params) => {
    setExternalTeamMemberModalOpen(false);
    setCurrentExternalTeamMember(null);
  };

  const handleOpenEditExternalTeamMembers = (applicant, index) => {
    setExternalTeamMemberModalOpen(true);
    setCurrentExternalTeamMember({ applicant, index });
  };

  const handleAddExternalTeamMember = (applicant) => {
    const temporaryAttendees = getValues()?.attendees ?? [];
    setValue("attendees", [...temporaryAttendees, applicant], { shouldDirty: true });
  };

  const handleEditExternalTeamMember = ({ index, data }) => {
    const temporaryAttendees = getValues()?.attendees ?? [];
    const currentEditedAttendee = temporaryAttendees[index];
    if (currentEditedAttendee) {
      setValue(
        "attendees",
        temporaryAttendees.map((att, idx) => {
          if (idx === index) {
            return {
              ...att,
              ...data,
            };
          }
          return att;
        }),
        { shouldDirty: true }
      );
    }
  };

  const handleOpenNotes = (user) => {
    if (user?._id === selectedUser?._id) setSelectedUser(null);
    else setSelectedUser(user);
  };

  const handleToggleAvailabilityBlock = async (row, blocksAvailability) => {
    await updateUserAvailabilityBlockMutation.mutateAsync({
      id: row?._id,
      blocksAvailability,
      eventUrl: currentEvent?.eventUrl,
    });
    const attendees = (getValues("attendees") ?? []).map((att) => {
      if (att._id === row._id)
        return {
          ...att,
          interview: {
            ...att.interview,
            blocksAvailability: blocksAvailability ? "Yes" : "No",
          },
        };
      return att;
    });
    setValue("attendees", attendees, { shouldDirty: false });
    currentEvent.attendees = attendees;
  };

  return (
    currentEvent && (
      <form onSubmit={handleSubmit(editEventFormHandler)}>
        <CustomCard
          icon={<Diversity3 color="white" />}
          cardTitle={`Interview Attendees: ${currentEvent?.eventName}`}
          cardActions={isDirty && <PanelActions onCancel={cancelChangesHandler} />}
          {...customPanelCard(isMobile, "warning")}
        >
          <Box sx={styles.container}>
            <Grid item xs={12}>
              <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
                Invited Applicants
              </MDTypography>
              <Grid container item>
                <Grid item flexBasis={0} flexGrow={1}>
                  <Searchbar
                    fetch={(params) =>
                      fetchApplicants({
                        ...params,
                        availableForInterviewJobSlug: currentEvent?.jobSlug,
                      })
                    }
                    fetchAll={false}
                    placeholder="Add Applicants"
                    columns={searchColumns}
                    queryCharacterLimit={3}
                    resultsLimit={15}
                    setFilters={() => {}}
                    filters={null}
                    searchBy={["firstName", "lastName", "email", "altEmail"]}
                    filterBy="_id"
                    scrollLimit={200}
                    onRowClick={handleAddApplicant}
                    formatData={formatSearchResults}
                    useFormStyle
                    paddingRight={0}
                  />
                </Grid>
                <Grid item display="flex" alignItems="center" justifyContent="center">
                  <Tooltip title="View available candidates" placement="top">
                    <IconButton
                      color="warning"
                      onClick={() => setAddApplicantModalOpen(true)}
                      sx={{ flexShrink: 0, padding: 1 }}
                    >
                      <PersonAdd />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item display="flex" alignItems="center" justifyContent="center">
                  <Tooltip title="Send message to applicants" placement="top">
                    <IconButton
                      color="violet"
                      onClick={() => handlePressBulkMessageButton("applicants")}
                      sx={{ flexShrink: 0, padding: 1 }}
                    >
                      <Mail />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

              <DataList
                renderRow={(row, idx) => (
                  <InterviewApplicantRow
                    row={row}
                    idx={idx}
                    onRemove={handleRemoveApplicant}
                    applicants={currentEvent?.applicants}
                    onClickRow={() =>
                      selectedApplicantIndex === idx
                        ? setSelectedApplicantIndex(null)
                        : setSelectedApplicantIndex(idx)
                    }
                    refetchApplicants={refetchApplicants}
                    customAttachments={jobAttachments}
                    customTemplates={
                      currentEvent?.description
                        ? [
                            {
                              name: "<< Interview Description >>",
                              subject: "Interview Invitation",
                              body: `<p>${currentEvent?.description}</p>`,
                            },
                          ]
                        : null
                    }
                    onChangeFeedback={onChangeFeedback}
                  />
                )}
                data={enrichedApplicants}
                greybar
                divider
                scrollY
                tableHeight={200}
                height={250}
                isLoading={isLoading}
                selected={selectedApplicantIndex}
                noPadding
              />
            </Grid>
            <Grid item xs={12} mt={2}>
              <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
                Invited Team Members
              </MDTypography>
              <Grid container item>
                <Grid item flexBasis={0} flexGrow={1}>
                  <Searchbar
                    fetch={fetchUsers}
                    fetchAll={false}
                    placeholder="Add Team Members"
                    columns={searchColumnsUsers}
                    queryCharacterLimit={3}
                    resultsLimit={15}
                    setFilters={() => {}}
                    filters={{ userType: "Master;Admin" }}
                    searchBy={["firstName", "lastName", "emailAddress"]}
                    filterBy="_id"
                    scrollLimit={200}
                    onRowClick={(item) => {
                      if (
                        !getValues("attendees").find((user) => {
                          const userId = user._id || user.id;
                          return userId === item._id;
                        })
                      ) {
                        setValue("attendees", [...getValues("attendees"), item], {
                          shouldDirty: true,
                        });
                        if (isEdit) setReminderToSaveChangesModalOpen(true);
                      } else enqueueSnackbar("User is already on the list", { variant: "error" });
                    }}
                    formatData={formatSearchResults}
                    useFormStyle
                    paddingRight={0}
                  />
                </Grid>
                <Grid item display="flex" alignItems="center" justifyContent="center">
                  <Tooltip title="Add external team members" placement="top">
                    <IconButton
                      color="error"
                      onClick={handleOpenAddExternalTeamMembers}
                      sx={{ flexShrink: 0, padding: 1 }}
                    >
                      <PersonAdd />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item display="flex" alignItems="center" justifyContent="center">
                  <Tooltip title="Send message to team members" placement="top">
                    <IconButton
                      color="violet"
                      onClick={() => handlePressBulkMessageButton("attendees")}
                      sx={{ flexShrink: 0, padding: 1 }}
                    >
                      <Mail />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

              <DataList
                renderRow={(row, idx) => (
                  <InterviewTeamMemberRow
                    row={row}
                    idx={idx}
                    onRemove={removeAttendee}
                    applicants={currentEvent?.attendees}
                    onChangeFeedback={onChangeFeedback}
                    selectedApplicant={
                      selectedApplicantIndex != null
                        ? (enrichedApplicants ?? [])[selectedApplicantIndex]
                        : null
                    }
                    selectedApplicantIndex={selectedApplicantIndex}
                    onEditExternalMember={() => handleOpenEditExternalTeamMembers(row, idx)}
                    onOpenNotes={handleOpenNotes}
                    isNotesOpen={row?._id === selectedUser?._id}
                    event={currentEvent}
                    onToggleAvailabilityBlock={handleToggleAvailabilityBlock}
                  />
                )}
                data={watch("attendees")}
                greybar
                divider
                scrollY
                tableHeight={200}
                height={250}
                isLoading={false}
                noPadding
              />
            </Grid>
            {selectedApplicantIndex != null && (
              <MDTypography component="label" variant="text" color="info">
                {`Showing feedback for ${
                  (enrichedApplicants ?? [])[selectedApplicantIndex]?.firstName
                } ${(enrichedApplicants ?? [])[selectedApplicantIndex]?.lastName}`}
              </MDTypography>
            )}
          </Box>
        </CustomCard>
        <ConfirmationModal
          isOpen={!!confirmationModalMode}
          onClose={() => {
            setBulkMessageModalCurrentMode(confirmationModalMode);
            setConfirmationModalMode(null);
          }}
          title={`You have unsaved changes. ${
            confirmationModalMode === "attendees" ? "Team Members" : "Applicants"
          } need to be saved before you can send messages to them. Do you want to save now?`}
          onConfirm={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(editEventFormHandler)();
            setBulkMessageModalCurrentMode(confirmationModalMode);
            setConfirmationModalMode(null);
          }}
        />
        <ConfirmPanelChanges onConfirm={handleConfirmChanges} />
        {!!addApplicantModalOpen && (
          <InterviewAddApplicantModal
            open={addApplicantModalOpen}
            setOpen={setAddApplicantModalOpen}
            jobSlug={currentEvent?.jobSlug}
            onAdd={handleAddApplicant}
            currentApplicants={watch("applicants")}
          />
        )}
        {!!bulkMessageModalCurrentMode && (
          <BulkMessagingModal
            applicants={
              bulkMessageModalCurrentMode === "applicants" ? applicantInfoList : teamMemberInfoList
            }
            isOpen={!!bulkMessageModalCurrentMode}
            toggleModal={() => setBulkMessageModalCurrentMode(null)}
            currentEvent={currentEvent}
            messageContext="Event"
            attachmentContext={bulkMessageModalCurrentMode === "applicants" ? "Event" : "Custom"}
            customAttachments={
              bulkMessageModalCurrentMode === "applicants" ? jobAttachments : applicantAttachments
            }
            customTemplates={
              currentEvent?.description
                ? [
                    {
                      name: "<< Interview Description >>",
                      subject: "Interview Invitation",
                      body: `<p>${currentEvent?.description}</p>`,
                    },
                  ]
                : null
            }
          />
        )}
        {!!addExternalTeamMemberModalOpen && (
          <InterviewAddExternalTeamMemberModal
            open={addExternalTeamMemberModalOpen}
            setOpen={handleCloseExternalTeamMembers}
            onAdd={handleAddExternalTeamMember}
            onEdit={handleEditExternalTeamMember}
            currentApplicant={currentExternalTeamMember?.applicant}
            currentApplicantIndex={currentExternalTeamMember?.index}
          />
        )}
      </form>
    )
  );
};

export default InterviewAttendees;
